import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Search as SearchIcon, Download as DownloadIcon } from '@mui/icons-material';
import { reportService, UnassignedClientCaseEntry } from '../services/reportService';
import { exportToCSV } from '../utils/csvExport';
import { useAuth } from '../contexts/AuthContext';
import { firmAPI } from '../services/api';
import { isPmpOrSiteAdminUser } from '../utils/firmAccess';

const today = new Date().toISOString().split('T')[0];

const UnassignedClientCasesReport: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAuthorized = () => isPmpOrSiteAdminUser(user);

  const [date, setDate] = useState(today);
  const [clientFirmId, setClientFirmId] = useState<number | ''>('');
  const [clientFirms, setClientFirms] = useState<any[]>([]);
  const [reportData, setReportData] = useState<UnassignedClientCaseEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user && !isAuthorized()) {
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    const loadFirms = async () => {
      try {
        const res = await firmAPI.getAll({ type: 'client', per_page: 1000 });
        setClientFirms((res.data.data?.data || res.data.data || []).filter((f: any) => f.type === 'client'));
      } catch (err) {
        console.error('Failed to load firms:', err);
      }
    };
    if (isAuthorized()) loadFirms();
  }, [user]);

  const handleSearch = async () => {
    setLoading(true);
    setError('');
    try {
      const params: Record<string, any> = { date };
      if (clientFirmId) params.client_firm_id = clientFirmId;
      const result = await reportService.getUnassignedClientCasesReport(params);
      setReportData(result.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch report');
    } finally {
      setLoading(false);
    }
  };

  if (user && !isAuthorized()) {
    return <Alert severity="error">Only PMP Admins and Site Admins can view this report.</Alert>;
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Unassigned Client Cases</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Cases created by client users that are not assigned to a person or vendor.
      </Typography>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField label="Date" type="date" value={date} onChange={(e) => setDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          <FormControl sx={{ minWidth: 220 }}>
            <InputLabel>Client firm</InputLabel>
            <Select label="Client firm" value={clientFirmId} onChange={(e) => setClientFirmId(e.target.value as number | '')}>
              <MenuItem value="">All</MenuItem>
              {clientFirms.map((firm) => (
                <MenuItem key={firm.id} value={firm.id}>{firm.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="contained" startIcon={<SearchIcon />} onClick={handleSearch} disabled={loading}>
            {loading ? <CircularProgress size={18} /> : 'Search'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            disabled={!reportData.length}
            onClick={() => exportToCSV(reportData, [
              { key: 'id', label: 'Case ID' },
              { key: 'title', label: 'Title' },
              { key: 'file_number', label: 'File Number' },
              { key: 'client_reference_id', label: 'Client ID' },
              { key: 'status', label: 'Status' },
              { key: 'created_at', label: 'Created At' },
              { key: 'creator_name', label: 'Created By' },
              { key: 'project_name', label: 'Project' },
              { key: 'client_firm_name', label: 'Client Firm' },
            ], `unassigned-client-cases_${date}.csv`)}
          >
            Export
          </Button>
        </Box>
      </Paper>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Case</TableCell>
              <TableCell>Client ID</TableCell>
              <TableCell>Created by</TableCell>
              <TableCell>Firm / Project</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reportData.map((row) => (
              <TableRow key={row.id}>
                <TableCell>
                  <Link href="#" onClick={(e) => { e.preventDefault(); if (row.client_firm_id) navigate(`/firms/${row.client_firm_id}/cases/${row.id}`); }}>
                    #{row.id} {row.title}
                  </Link>
                </TableCell>
                <TableCell>{row.client_reference_id || '—'}</TableCell>
                <TableCell>{row.creator_name}</TableCell>
                <TableCell>{row.client_firm_name} / {row.project_name}</TableCell>
                <TableCell>{row.status}</TableCell>
                <TableCell>{row.created_at}</TableCell>
              </TableRow>
            ))}
            {!loading && reportData.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>No unassigned client-user cases for this date.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default UnassignedClientCasesReport;
